import * as Sentry from '@sentry/react';

// GlitchTip (Sentry protocol) telemetry - replaces the App Insights browser
// SDK (#134). The event-type enum keeps its historical name so the ~40
// existing call sites (including useCaptureDeeplinkTrace, whose events are
// the #150 incident evidence) are unchanged: same names, same properties,
// now emitted as GlitchTip structured logs + breadcrumbs, with ERROR/FATAL
// additionally captured as GlitchTip issues.
export enum APP_INSIGHTS_EVENT_TYPE {
  ERROR = 'KWELLO_ERROR',
  WARNING = 'KWELLO_WARNING',
  INFO = 'KWELLO_INFO',
  SUCCESS = 'KWELLO_SUCCESS',
  TRACE = 'KWELLO_TRACE',
  DEBUG = 'KWELLO_DEBUG',
  FATAL = 'KWELLO_FATAL',
}

export interface CustomEventProperties {
  userId: string;
  email: string;
  companyName: string;
  projectId: string;
  [key: string]: any;
}

export interface TrackEventArgs {
  type: APP_INSIGHTS_EVENT_TYPE;
  name: string;
  properties: CustomEventProperties;
}

// No DSN = telemetry fully disabled (mirrors the backend contract).
export const isTelemetryEnabled = (): boolean => Boolean(import.meta.env.VITE_GLITCHTIP_DSN);

export const initTelemetry = () => {
  if (!isTelemetryEnabled()) return;

  Sentry.init({
    dsn: import.meta.env.VITE_GLITCHTIP_DSN,
    environment: import.meta.env.VITE_STAGE || 'unknown',
    integrations: [Sentry.browserTracingIntegration()],
    // || not ??: an unset Vite var arrives as the empty string, and Number('') is 0 —
    // which would silently disable tracing rather than default to full sampling.
    tracesSampleRate: Number(import.meta.env.VITE_GLITCHTIP_TRACES_SAMPLE_RATE || '1.0'),
    // Propagate sentry-trace/baggage to our API only (its CORS allows any
    // header), so browser->API traces correlate end to end.
    tracePropagationTargets: import.meta.env.VITE_API_BASE_URL
      ? [import.meta.env.VITE_API_BASE_URL]
      : [],
    // GlitchTip's searchable-logs feature ingests these. Its Logs-view
    // Service filter binds to the OTel service.name attribute per record.
    enableLogs: true,
    beforeSendLog(log) {
      return { ...log, attributes: { ...log.attributes, 'service.name': 'nextgen-web' } };
    },
    // Redaction policy (#134): no PII defaults; strip cookies/auth headers
    // from anything the SDK collected.
    sendDefaultPii: false,
    beforeSend(event) {
      if (event.request) {
        delete event.request.cookies;
        if (event.request.headers) {
          delete event.request.headers.Authorization;
          delete event.request.headers.authorization;
          delete event.request.headers.Cookie;
          delete event.request.headers.cookie;
        }
      }
      return event;
    },
  });
};

const LOG_LEVEL_BY_TYPE: Record<APP_INSIGHTS_EVENT_TYPE, 'error' | 'warn' | 'info' | 'debug'> = {
  [APP_INSIGHTS_EVENT_TYPE.ERROR]: 'error',
  [APP_INSIGHTS_EVENT_TYPE.FATAL]: 'error',
  [APP_INSIGHTS_EVENT_TYPE.WARNING]: 'warn',
  [APP_INSIGHTS_EVENT_TYPE.INFO]: 'info',
  [APP_INSIGHTS_EVENT_TYPE.SUCCESS]: 'info',
  [APP_INSIGHTS_EVENT_TYPE.TRACE]: 'debug',
  [APP_INSIGHTS_EVENT_TYPE.DEBUG]: 'debug',
};

/** Longest string value forwarded as a log attribute. */
const MAX_ATTRIBUTE_LENGTH = 512;

/**
 * Redaction policy (#134) for structured-log attributes and breadcrumb data.
 *
 * Call sites pass whole domain objects — an `insight`, a `SurveyAnswer`, a save
 * result. Those trees carry `freeText`, `textInputValue` and `base64String`,
 * i.e. verbatim HCP interaction text and entire images. Serialising them whole
 * put that in a 90-day searchable log store on a shared GlitchTip instance, and
 * a stringified image would blow the attribute size limit and get the envelope
 * truncated or rejected anyway.
 *
 * So: scalars pass through (truncated), and anything structured is reduced to a
 * shape summary. Identifiers, counts and trace ids — everything the deeplink
 * reconstruction actually needs — are scalars and survive untouched.
 */
export const toAttributes = (properties: Record<string, any>): Record<string, string> =>
  Object.fromEntries(
    Object.entries(properties ?? {}).map(([key, value]) => {
      if (value === null || value === undefined) return [key, 'null'];

      if (typeof value === 'string') {
        return [
          key,
          value.length > MAX_ATTRIBUTE_LENGTH
            ? `${value.slice(0, MAX_ATTRIBUTE_LENGTH)}…[truncated]`
            : value,
        ];
      }

      if (typeof value === 'number' || typeof value === 'boolean') {
        return [key, String(value)];
      }

      if (Array.isArray(value)) return [key, `[array: ${value.length} items]`];

      return [key, `[object: ${Object.keys(value).length} keys]`];
    })
  );

export const trackCustomEvent = ({ type, name, properties }: TrackEventArgs) => {
  if (!isTelemetryEnabled()) return;

  const url = window.location.href;
  const attributes = { ...toAttributes(properties), url, eventType: type };
  const message = `${type}: ${name}`;

  // Structured log record - searchable in GlitchTip Logs by any attribute
  // (this is what makes captureDeeplinkTraceId reconstruction work).
  Sentry.logger[LOG_LEVEL_BY_TYPE[type]](message, attributes);

  // Breadcrumb so the event trail is attached to any later error.
  Sentry.addBreadcrumb({
    category: 'kwello.event',
    message,
    level: type === APP_INSIGHTS_EVENT_TYPE.WARNING ? 'warning' : 'info',
    data: attributes,
  });

  // Error-grade custom events become GlitchTip issues, preserving the App
  // Insights behaviour where KWELLO_ERROR/FATAL events were alert-worthy.
  if (type === APP_INSIGHTS_EVENT_TYPE.ERROR || type === APP_INSIGHTS_EVENT_TYPE.FATAL) {
    Sentry.captureMessage(message, {
      level: type === APP_INSIGHTS_EVENT_TYPE.FATAL ? 'fatal' : 'error',
      extra: attributes,
    });
  }
};

// Identify the authenticated user on the Sentry scope (#134 user policy:
// id + email are permitted context).
export const identifyTelemetryUser = (user: { id?: string; email?: string }) => {
  if (!isTelemetryEnabled()) return;
  Sentry.setUser(user.id || user.email ? { id: user.id, email: user.email } : null);
};

// Root error-boundary hook: report render crashes as GlitchTip issues.
export const captureTelemetryException = (error: unknown) => {
  if (!isTelemetryEnabled()) return;
  Sentry.captureException(error);
};
